import { useOs } from '@wppopen/react'
import { BrowserRouter } from 'react-router-dom'
import { App } from './App'
import { useEffect, useMemo, useState } from 'react'
import useApi from 'hooks/useApi'
import { AuthoriseCreateData } from '@contracts'
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner'
import { AppErrorType } from '@wppopen/core'
import useOsContext from 'hooks/useOsContext'
import { AppProvider } from 'context/appContext'
import NotAuthorisedPage from 'pages/NotAuthorisedPage/NotAuthorisedPage'
import { getAppLaunchEventDataObject } from 'utils/gtm/appLaunchEvent'
import { NavigationProvider } from 'context/navigationContext/navigationContext'

export function OSApp() {
  /**
   * This hook is provided by the utility package for more convenient work with OS context
   * @return osContext
   * - is a subscribe function that returns you the main context object with MicroAppContext data
   * @return osApi
   * - methods to fire actions on the parent OS level
   *
   * Read more about it in the README file
   */
  const osContextValue = useOs()
  const [canAccessApp, setCanAccessApp] = useState<boolean>(false)
  const { tenantId, account_ids, bearerHeader } = useOsContext({ wppOpenOsContextOverride: osContextValue })
  const [errorStatus, setErrorStatus] = useState<number | null>(null)

  const { fetchData: fetchAuth, url: authFetchUrl } = useApi<AuthoriseCreateData>(`${'authorise'}`, {}, 0, 'json', true)

  useEffect(() => {
    if (!tenantId) return

    const headers: HeadersInit = {
      ...bearerHeader,
      ...account_ids,
    }

    fetchAuth(authFetchUrl, {
      method: 'POST',
      headers,
    })
      .then(data => {
        if (data instanceof Response) {
          setErrorStatus(data.status)
          return
        }
        if (data.status === 'success') {
          setCanAccessApp(true)
        } else if (data.status === 'error') {
          // manually handle the
          setCanAccessApp(false)
          osContextValue.osApi.navigation.triggerError(AppErrorType.ForbiddenPage)
        }
      })
      .catch(err => {
        console.error(err)
        setCanAccessApp(false)
        osContextValue.osApi.navigation.triggerError(AppErrorType.CriticalError)
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osContextValue.osApi, authFetchUrl])

  const customErrorPage = useMemo(() => {
    if (!errorStatus) return null
    switch (errorStatus) {
      case 401:
        return <NotAuthorisedPage />
      default:
        return <NotAuthorisedPage />
    }
  }, [errorStatus])

  useEffect(() => {
    if (!canAccessApp) return
    const appLaunchEventData = getAppLaunchEventDataObject(osContextValue)
    window.wppopen_dataLayer.push(appLaunchEventData)
  }, [canAccessApp])
  return !canAccessApp && !errorStatus ? (
    <div>
      <LoadingSpinner dataTestId="auth-loading-spinner" />
    </div>
  ) : (
    <div>
      <BrowserRouter basename={osContextValue.osContext.baseUrl || ''}>
        <NavigationProvider osContextValue={osContextValue}>
          {!canAccessApp ? (
            customErrorPage
          ) : (
            <AppProvider osContextValue={osContextValue}>
              <App />
            </AppProvider>
          )}
        </NavigationProvider>
      </BrowserRouter>
    </div>
  )
}
